// @flow

import React from 'react';
import * as Immutable from 'immutable';
import moment from 'moment';
import { Segment, Rating, Label } from 'semantic-ui-react';
import { AutoSizer, Table as FlexTable, Column } from 'react-virtualized';
import { ListLoader } from '../shared';
import NoThread from './NoThread';
import NoResult from './NoResult';
import type { Thread } from '../../types';
import type { FilterState } from '../../reducers/threads';

type Props = {
  history: any,
  threads: Immutable.List<Thread>,
  isFiltering: boolean,
  filter: FilterState,
  onTrack: (id: string, value: boolean) => void,
};

const ROW_HEIGHT: number = 65;

const computeListHeight = (rowCount, rowHeight, height) =>
  Math.min(rowCount * rowHeight, height);

const hasFilter = (filter: FilterState): boolean => {
  return (
    (filter.search != null && filter.search.length > 0) ||
    filter.unread ||
    filter.tracked
  );
};

export const List = ({
  history,
  threads,
  isFiltering,
  filter,
  onTrack,
}: Props) => {
  if (isFiltering) {
    return <ListLoader title="Chargement des conversations..." />;
  }

  if (hasFilter(filter) && threads.size === 0) {
    return <NoResult />;
  }

  if (threads.size === 0) {
    return <NoThread />;
  }

  const navigateToThread = ({ rowData }) =>
    history.push(`/threads/${rowData.id}/messages`);

  const getRow = ({ index }) => threads.get(index);

  return (
    <div className="virtualized-container">
      <AutoSizer>
        {({ width, height }) => (
          <Segment
            style={{
              width,
              height: computeListHeight(threads.size, ROW_HEIGHT, height),
            }}
          >
            <AutoSizer>
              {segmentDimentions => (
                <FlexTable
                  ref="Table"
                  width={segmentDimentions.width}
                  height={computeListHeight(
                    threads.size,
                    ROW_HEIGHT,
                    segmentDimentions.height
                  )}
                  rowHeight={ROW_HEIGHT}
                  rowCount={threads.size}
                  rowGetter={getRow}
                  onRowClick={navigateToThread}
                >
                  <Column
                    dataKey="tracked"
                    cellRenderer={trackedRowRenderer(onTrack)}
                    className="tracked-column"
                    width={1}
                  />
                  <Column
                    dataKey="content"
                    cellRenderer={userAndContentRenderer}
                    className="user-and-content-column"
                    width={1}
                  />
                  <Column
                    dataKey="unreadCount"
                    cellRenderer={dateAndUnreadRowRenderer}
                    className="date-and-unread-column"
                    width={1}
                  />
                </FlexTable>
              )}
            </AutoSizer>
          </Segment>
        )}
      </AutoSizer>
    </div>
  );
};

const strongIfUnread = rowData => component => {
  if (rowData.unreadCount > 0) {
    return <strong>{component}</strong>;
  } else {
    return component;
  }
};

const trackedRowRenderer = onTrack => ({ rowData, cellData }) => {
  const track = event => {
    event.preventDefault();
    event.stopPropagation();

    onTrack(rowData.id, !cellData);
  };

  return <Rating icon="star" rating={cellData ? 1 : 0} onClick={track} />;
};

const userAndContentRenderer = ({ rowData }) => {
  return (
    <div>
      <div>
        {userRowRenderer({
          cellData: rowData.user,
          rowData,
        })}
      </div>
      <div>{contentRowRenderer({ rowData })}</div>
    </div>
  );
};

const userRowRenderer = ({ cellData, rowData }) => {
  return strongIfUnread(rowData)(cellData);
};

const contentRowRenderer = ({ rowData }) => {
  const { lastMessage } = rowData;
  if (lastMessage != null) {
    return strongIfUnread(rowData)(lastMessage.content);
  } else {
    return null;
  }
};

const dateAndUnreadRowRenderer = ({ rowData }) => {
  return (
    <div>
      <div>{dateRowRenderer({ rowData })}</div>
      {unreadRowRenderer({ cellData: rowData.unreadCount, rowData })}
    </div>
  );
};

const dateRowRenderer = ({ rowData }) => {
  const { lastMessage } = rowData;
  if (lastMessage != null) {
    const { date } = lastMessage;

    let format;
    if (date.isSame(moment(), 'day')) {
      // If today only displaying hour
      format = 'HH:mm';
    } else {
      // Otherwise only day
      format = 'DD MMM YYYY';
    }

    return strongIfUnread(rowData)(date.format(format));
  } else {
    return null;
  }
};

const unreadRowRenderer = ({ cellData }) => {
  if (cellData > 0) {
    return (
      <div>
        <Label circular color="orange">
          {cellData}
        </Label>
      </div>
    );
  } else {
    return null;
  }
};



// WEBPACK FOOTER //
// ./src/modules/messaging/components/threads/List.js